import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { isValid, parseISO } from 'date-fns'
import { AppShell } from '@/components/layout/AppShell'
import { ErrorState } from '@/components/ui/ErrorState'
import { authKeys, fetchMe } from '@/lib/auth'
import { storedTeamView } from '@/lib/team-view'
import { todayIsoDate } from '@/lib/week'
import { ClientDetailPage } from '@/pages/client-detail'
import { VooruitblikPage } from '@/pages/Vooruitblik'
import { ClientsPage } from '@/pages/Clients'
import { DashboardPage } from '@/pages/Dashboard'
import { InvoiceDetailPage } from '@/pages/InvoiceDetail'
import { InvoiceNewPage } from '@/pages/InvoiceNew'
import { InvoicesPage } from '@/pages/Invoices'
import { LoginPage } from '@/pages/Login'
import { MyPayoutsPage } from '@/pages/MyPayouts'
import { PayoutsAdminPage } from '@/pages/PayoutsAdmin'
import { RecurringPage } from '@/pages/Recurring'
import { RecurringDetailPage } from '@/pages/RecurringDetail'
import { ReportsPage } from '@/pages/Reports'
import { SettingsPage } from '@/pages/settings'
import { TimesheetPage } from '@/pages/Timesheet'
import { TimesheetTeamPage } from '@/pages/TimesheetTeam'
import type { UserRole } from '@/lib/types'

export interface RouterContext {
  queryClient: QueryClient
}

interface GuardContext {
  me?: { user: { role?: UserRole; personScoped?: boolean } } | null
}

function fallbackRoute(context: GuardContext): '/uren' | '/' {
  return context.me?.user.personScoped ? '/uren' : '/'
}

function requireFinance({ context }: { context: GuardContext }): void {
  const role = context.me?.user.role
  if (role !== 'ADMIN' && role !== 'PARTNER') throw redirect({ to: fallbackRoute(context) })
}

const rootRoute = createRootRouteWithContext<RouterContext>()()

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { error?: string } => ({
    error: typeof search.error === 'string' ? search.error : undefined,
  }),
})

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData({ queryKey: authKeys.me, queryFn: fetchMe })
    if (!me) throw redirect({ to: '/login' })
    return { me }
  },
  component: AppShell,
})

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    // Gast = project-scoped gebruiker: geen STAFF-toegang tot het dashboard.
    // Stuur naar de eigen werkplek (urenregistratie) i.p.v. een 403 op /.
    if (context.me?.user.personScoped) throw redirect({ to: '/uren' })
  },
  component: DashboardPage,
  validateSearch: (search: Record<string, unknown>): { scope?: string; person?: string } => ({
    scope: typeof search.scope === 'string' ? search.scope : undefined,
    person: typeof search.person === 'string' ? search.person : undefined,
  }),
})
const timesheetRoute = createRoute({ getParentRoute: () => authedRoute, path: '/uren', component: TimesheetPage })
// Het teamrooster toont uren, week-akkoord en declarabiliteit van collega's.
/**
 * De weergavevoorkeur geldt alleen bij een kale /uren/team: een link met parameters
 * beschrijft al wat hij wil tonen. Hier en niet in een effect op de pagina, want dan
 * tekent de weekweergave zich eerst en haalt hij zijn teamdata op voor niets.
 */
function teamViewPreference({ search, context }: { search: TeamSearch; context: GuardContext }): void {
  requireFinance({ context })
  const bare = !search.date && !search.week && !search.person && !search.focus
  if (!bare || storedTeamView() !== 'day') return
  throw redirect({ to: '/uren/team', search: { date: todayIsoDate() }, replace: true })
}

interface TeamSearch {
  person?: string
  week?: string
  focus?: boolean
  date?: string
}

const timesheetTeamRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/uren/team',
  beforeLoad: teamViewPreference,
  component: TimesheetTeamPage,
  // Een verminkte week zou een kop opleveren die niet bij de data hoort: weekMetaFromId
  // valt stil terug op de huidige week en begrenst het weeknummer niet.
  validateSearch: (search: Record<string, unknown>): TeamSearch => {
    const week = typeof search.week === 'string' && /^\d{4}-W(0?[1-9]|[1-4]\d|5[0-3])$/.test(search.week) ? search.week : undefined
    const person = typeof search.person === 'string' && search.person !== '' ? search.person : undefined
    // De aanwezigheid van date kiest de dagweergave, dus een datum die niet bestaat
    // (2026-02-30) valt weg in plaats van door te rollen naar een andere dag.
    const date =
      typeof search.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(search.date) && isValid(parseISO(search.date))
        ? search.date
        : undefined
    return { person, week, focus: search.focus ? true : undefined, date }
  },
})
const invoicesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/facturen',
  beforeLoad: requireFinance,
  component: InvoicesPage,
  validateSearch: (search: Record<string, unknown>): { wizard?: boolean; bucket?: string; status?: string } => ({
    wizard: search.wizard ? true : undefined,
    bucket: typeof search.bucket === 'string' ? search.bucket : undefined,
    status: typeof search.status === 'string' ? search.status : undefined,
  }),
})
const invoiceNewRoute = createRoute({ getParentRoute: () => authedRoute, path: '/facturen/nieuw', beforeLoad: requireFinance, component: InvoiceNewPage })
const invoiceDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/facturen/$id',
  beforeLoad: requireFinance,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
  component: function InvoiceDetailRoute() {
    const { id } = invoiceDetailRoute.useParams()
    return <InvoiceDetailPage invoiceId={id} />
  },
})
const recurringRoute = createRoute({ getParentRoute: () => authedRoute, path: '/terugkerend', beforeLoad: requireFinance, component: RecurringPage })
const recurringDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/terugkerend/$id',
  beforeLoad: requireFinance,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
  component: function RecurringDetailRoute() {
    const { id } = recurringDetailRoute.useParams()
    return <RecurringDetailPage recurringId={id} />
  },
})
const clientsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/klanten', beforeLoad: requireFinance, component: ClientsPage })
const clientDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/klanten/$id',
  beforeLoad: requireFinance,
  component: function ClientDetailRoute() {
    const { id } = clientDetailRoute.useParams()
    return <ClientDetailPage clientId={id} />
  },
})
const myPayoutsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/mijn-uitbetalingen',
  beforeLoad: ({ context }) => {
    if (context.me?.user.personScoped) throw redirect({ to: '/uren' })
  },
  component: MyPayoutsPage,
})
const payoutsAdminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/uitbetalingen',
  beforeLoad: requireFinance,
  component: PayoutsAdminPage,
  validateSearch: (search: Record<string, unknown>): { scope?: string } => ({
    scope: typeof search.scope === 'string' ? search.scope : undefined,
  }),
})
const vooruitblikRoute = createRoute({ getParentRoute: () => authedRoute, path: '/vooruitblik', beforeLoad: requireFinance, component: VooruitblikPage })
const reportsRoute = createRoute({ getParentRoute: () => authedRoute, path: '/rapportages', beforeLoad: requireFinance, component: ReportsPage })
const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/instellingen',
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>): { tab?: string } => ({
    tab: typeof search.tab === 'string' ? search.tab : undefined,
  }),
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  authedRoute.addChildren([
    dashboardRoute,
    timesheetRoute,
    timesheetTeamRoute,
    invoicesRoute,
    invoiceNewRoute,
    invoiceDetailRoute,
    recurringRoute,
    recurringDetailRoute,
    clientsRoute,
    clientDetailRoute,
    myPayoutsRoute,
    payoutsAdminRoute,
    vooruitblikRoute,
    reportsRoute,
    settingsRoute,
  ]),
])

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
    defaultErrorComponent: ({ error, reset }) => <ErrorState error={error} onRetry={reset} />,
  })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }
}
